// utils/formGrid/limits.js
//
// HOW WIDE A FORM ELEMENT MAY BE — the type-limits table of the form layout
// grid (KPE-023; the epic's Spec, ruled by the owner 2026-10-10). A form lays
// its elements out on 12 columns; each element asks for a `span` of them, or
// `auto`, and this one table says, per data type, the narrowest it may be, the
// width `auto` means, and the widest. The renderer draws from it, the layout
// editor offers exactly what it allows, and the API refuses a span outside it
// when a layout is saved, so the three cannot disagree.
//
// DEPENDENCY-FREE AND PLATFORM-FREE ON PURPOSE. This file is vendored
// byte-identical into keepr-web (`src/utils/formGrid/`) with the vector file
// beside it (`vectors.json`), which both repos' suites drive, and
// `test/vendoredParity.test.js` compares the two mains. Nothing here may
// require() anything, and there is NO SPREAD SYNTAX: Babel would inject a
// helper import for it, turning this CommonJS file into an ES module with no
// default export in the web build (knowledge/history/calculated-elements.md).
//
// The table, as ruled (min · Auto · max, in columns of 12):
//
//   yes/no · number (every numeric type, an auto-number too)     2 · 3 · 6
//   date · time · rating                                         3 · 4 · 6
//   date and time                                                4 · 4 · 6
//   currency · measurement                                       3 · 4 · 6
//   choice shown as buttons (2 to 5 options)    from its options · the same · 12
//   choice in a dropdown · short text                            3 · 6 · 12
//   one item lookup · one person · location                      4 · 6 · 12
//   several items or people · several dates · tags               6 · 12 · 12
//   long text · rich text (never under half) · files and images  6 · 12 · 12
//
// WHAT `auto` MEANS. The type's Auto width from this table, and never below
// the element's minimum. A choice shown as buttons is the one row whose Auto
// is its minimum: it follows the number of options (3 options narrower than 5),
// so the editor can show the footprint the element really has. Nothing is read
// from how wide a form happened to be drawn before, so every existing form
// tightens when the renderer ships.
//
// A WIDTH A TYPE CAN NO LONGER HOLD is clamped, never refused: changing an
// element's type must not stop its card from saving. `resolveSpan` clamps
// when the form is drawn; the API refuses a span outside the limits only
// when a LAYOUT is saved, and says so by element and range.
//
// Types the epic's table does not name (time, rating, color, confirmations,
// location, person, several dates; "ID" is not a data type: an auto-number is
// a number with `options.sequence`) take the limits of the nearest row by what
// they draw. They are one-line edits here, and the vectors hold them.
//
// A CALCULATED element is its result's data type already (its `dataType` is
// the type of what it calculates), so it needs no row of its own.

'use strict';

/** The grid is 12 columns wide: every sixth, and quarters. */
const COLUMNS = 12;

/** The width every element has until an author sets one. */
const AUTO = 'auto';

// A segmented choice needs room for each option on ONE row. The count of
// options is all the table can know (not their words), so each takes 1.2
// columns, rounded up, never under 3 (owner's ruling 2026-10-11, KPR-624;
// it was 1.5): two options need 3, three need 4, four need 5, five need 6.
// Six options are a dropdown, not buttons (isSegmentedChoice), so the formula
// never meets them. The arithmetic is done in tenths of a column so no float
// decides a boundary (3 * 1.2 is 3.5999999999999996 in JavaScript).
const SEGMENT_COLUMNS = 1.2;
const SEGMENT_TENTHS = 12;
const SEGMENT_FLOOR = 3;
const FEWEST_SEGMENTS = 2;
const MOST_SEGMENTS = 5;

// kind -> what the person calls it, then min, auto, max. The segmented row's
// min and auto are placeholders: both come from the element's options
// (segmentedMin).
const TABLE = Object.freeze({
	boolean: Object.freeze({ label: 'yes/no', min: 2, auto: 3, max: 6 }),
	number: Object.freeze({ label: 'number', min: 2, auto: 3, max: 6 }),
	date: Object.freeze({ label: 'date', min: 3, auto: 4, max: 6 }),
	datetime: Object.freeze({ label: 'date and time', min: 4, auto: 4, max: 6 }),
	time: Object.freeze({ label: 'time', min: 3, auto: 4, max: 6 }),
	money: Object.freeze({ label: 'currency', min: 3, auto: 4, max: 6 }),
	measurement: Object.freeze({ label: 'measurement', min: 3, auto: 4, max: 6 }),
	rating: Object.freeze({ label: 'rating', min: 3, auto: 4, max: 6 }),
	color: Object.freeze({ label: 'color', min: 2, auto: 3, max: 6 }),
	confirmations: Object.freeze({ label: 'me-too count', min: 2, auto: 3, max: 6 }),
	segmented: Object.freeze({ label: 'choice shown as buttons', min: SEGMENT_FLOOR, auto: SEGMENT_FLOOR, max: 12 }),
	dropdown: Object.freeze({ label: 'choice', min: 3, auto: 6, max: 12 }),
	text: Object.freeze({ label: 'short text', min: 3, auto: 6, max: 12 }),
	location: Object.freeze({ label: 'location', min: 4, auto: 6, max: 12 }),
	lookup: Object.freeze({ label: 'item lookup', min: 4, auto: 6, max: 12 }),
	user: Object.freeze({ label: 'person', min: 4, auto: 6, max: 12 }),
	lookupMany: Object.freeze({ label: 'item lookup holding several', min: 6, auto: 12, max: 12 }),
	userMany: Object.freeze({ label: 'person holding several', min: 6, auto: 12, max: 12 }),
	dateMany: Object.freeze({ label: 'several dates', min: 6, auto: 12, max: 12 }),
	tags: Object.freeze({ label: 'tags', min: 6, auto: 12, max: 12 }),
	longText: Object.freeze({ label: 'long text', min: 6, auto: 12, max: 12 }),
	richText: Object.freeze({ label: 'rich text', min: 6, auto: 12, max: 12 }),
	file: Object.freeze({ label: 'files and images', min: 6, auto: 12, max: 12 }),
});

// dataType -> kind, for the types that read the same however they are set up.
// The others (choice, card-lookup, user, date, date-time) are decided by the
// element's options in limitKindOf. A short text is a text box whatever its
// options hold: a choice list on it is inert (KPE-033). An absent or unknown
// dataType reads as short text, as every renderer reads it.
const TYPE_KINDS = Object.freeze({
	'text-small': 'text', email: 'text', url: 'text', phone: 'text',
	'text-large': 'longText', 'rich-text': 'richText',
	number: 'number', decimal: 'number', integer: 'number',
	boolean: 'boolean',
	currency: 'money', measurement: 'measurement',
	rating: 'rating', color: 'color', confirmations: 'confirmations',
	date: 'date', 'date-time': 'datetime', time: 'time',
	location: 'location',
	file: 'file',
	choice: 'dropdown',
	// Tags are not an element a layout places; the renderer asks for the row by this name.
	tags: 'tags',
	'card-lookup': 'lookup', user: 'user',
});

function hasOwn(obj, key) {
	return Object.prototype.hasOwnProperty.call(obj, key);
}

function optionsOf(element) {
	return element && element.options && typeof element.options === 'object' ? element.options : {};
}

function choiceCount(options) {
	return Array.isArray(options.choices) ? options.choices.length : 0;
}

/**
 * Does this element draw as buttons? The renderer's own rule
 * (keepr-web EventFormElement `isSegmented`): a single-valued choice with two
 * to five options and the icon-tile treatment not chosen. A choice that
 * allows several is a multiple select, whatever its length. A choice list
 * means something only on a choice (the owner's ruling, 2026-10-19, KPE-033):
 * a short text still carrying one — kept, inert, for a change back — is a text
 * box. Until KPR-1014 a short text with a list drew as a choice.
 */
function isSegmentedChoice(element) {
	const el = element || {};
	if (el.dataType !== 'choice') return false;
	const options = optionsOf(el);
	if (options.allowMultiple === true) return false;
	if (options.selectIcons || options.preferChoiceIcons) return false;
	const n = choiceCount(options);
	return n >= FEWEST_SEGMENTS && n <= MOST_SEGMENTS;
}

/** The columns `count` buttons need on one row (3 for two, 4 for three, 5 for four, 6 for five). */
function segmentedMin(count) {
	const n = typeof count === 'number' && isFinite(count) ? Math.floor(count) : 0;
	if (n < FEWEST_SEGMENTS) return SEGMENT_FLOOR;
	return Math.min(COLUMNS, Math.max(SEGMENT_FLOOR, Math.ceil((n * SEGMENT_TENTHS) / 10)));
}

/** Which row of the table an element falls in. */
function limitKindOf(element) {
	const el = element || {};
	const options = optionsOf(el);
	const dataType = typeof el.dataType === 'string' ? el.dataType : null;
	const many = options.allowMultiple === true;

	if (dataType === 'choice') {
		if (isSegmentedChoice(el)) return 'segmented';
		// A choice with no list is the plain text box the renderer falls back to.
		return choiceCount(options) > 0 ? 'dropdown' : 'text';
	}
	if (dataType === 'card-lookup') return many ? 'lookupMany' : 'lookup';
	if (dataType === 'user') return many ? 'userMany' : 'user';
	if ((dataType === 'date' || dataType === 'date-time') && many) return 'dateMany';
	return dataType !== null && hasOwn(TYPE_KINDS, dataType) ? TYPE_KINDS[dataType] : 'text';
}

/**
 * The limits of one element: `{ kind, label, min, auto, max }`. `auto` is the
 * width `auto` resolves to, already raised to the minimum and held under the
 * maximum, so a caller never has to repeat that rule.
 */
function limitsFor(element) {
	const kind = limitKindOf(element);
	const row = TABLE[kind];
	const min = kind === 'segmented' ? segmentedMin(choiceCount(optionsOf(element))) : row.min;
	const max = row.max;
	// A choice shown as buttons is as wide as its buttons need; every other row's Auto is the table's, raised to the minimum.
	const auto = kind === 'segmented' ? min : Math.min(max, Math.max(min, row.auto));
	return { kind: kind, label: row.label, min: min, auto: auto, max: max };
}

/** A whole number of columns (any whole number: the clamp judges the range). */
function isWholeSpan(span) {
	return typeof span === 'number' && isFinite(span) && Math.floor(span) === span;
}

/**
 * The width an element is drawn at: its `span` held to the limits, or for
 * `auto` (or no span, or anything that is not a whole number) the type's Auto
 * width, never below the minimum. Always a whole number from 1 to 12.
 */
function resolveSpan(element, span) {
	const limits = limitsFor(element);
	if (!isWholeSpan(span)) return limits.auto;
	return Math.min(limits.max, Math.max(limits.min, span));
}

/**
 * Is this span one the element may hold? `auto` (or none) always is.
 *
 *   { ok, span, effective, kind, label, min, auto, max, reason }
 *
 * `span` is what was asked ('auto' when none); `effective` is what the form
 * draws; `reason` is null when ok, else 'invalid' (not a whole number from 1
 * to 12), 'below' (under the minimum) or 'above' (over the maximum).
 */
function spanVerdict(element, span) {
	const limits = limitsFor(element);
	const asked = span === undefined || span === null || span === AUTO ? AUTO : span;
	const verdict = {
		ok: true, span: asked, effective: resolveSpan(element, asked),
		kind: limits.kind, label: limits.label, min: limits.min, auto: limits.auto, max: limits.max, reason: null,
	};
	if (asked === AUTO) return verdict;
	verdict.ok = false;
	if (!isWholeSpan(asked) || asked < 1 || asked > COLUMNS) verdict.reason = 'invalid';
	else if (asked < limits.min) verdict.reason = 'below';
	else if (asked > limits.max) verdict.reason = 'above';
	else verdict.ok = true;
	return verdict;
}

// ---------------------------------------------------------------- entries

/**
 * One entry of a form layout group's `elements`, read: either the bare element
 * name (auto width, no new row — every layout stored before the grid) or
 * `{ element, span?, newRow? }`. Always `{ element, span, newRow }`, with
 * `span` 'auto' when none and `newRow` a boolean. `element` is null for
 * anything that is neither shape.
 */
function entryOf(entry) {
	if (typeof entry === 'string') return { element: entry, span: AUTO, newRow: false };
	if (entry && typeof entry === 'object' && !Array.isArray(entry) && typeof entry.element === 'string') {
		const span = entry.span === undefined || entry.span === null ? AUTO : entry.span;
		return { element: entry.element, span: span, newRow: entry.newRow === true };
	}
	return { element: null, span: AUTO, newRow: false };
}

/** The element name an entry places, or null. */
function entryName(entry) {
	return entryOf(entry).element;
}

/**
 * The one spelling of an entry: the bare name when it asks for nothing (no
 * span or `auto`, no new row), else an object that carries only what it sets.
 * A layout read back and sent again therefore saves unchanged.
 */
function makeEntry(element, settings) {
	const s = settings || {};
	const hasSpan = s.span !== undefined && s.span !== null && s.span !== AUTO;
	const newRow = s.newRow === true;
	if (!hasSpan && !newRow) return element;
	const out = { element: element };
	if (hasSpan) out.span = s.span;
	if (newRow) out.newRow = true;
	return out;
}

module.exports = {
	COLUMNS: COLUMNS,
	AUTO: AUTO,
	TABLE: TABLE,
	SEGMENT_COLUMNS: SEGMENT_COLUMNS,
	isSegmentedChoice: isSegmentedChoice,
	segmentedMin: segmentedMin,
	limitKindOf: limitKindOf,
	limitsFor: limitsFor,
	resolveSpan: resolveSpan,
	spanVerdict: spanVerdict,
	entryOf: entryOf,
	entryName: entryName,
	makeEntry: makeEntry,
};
